/**
 * Command Centre — Sticky Sub-Navigation
 *
 * Horizontal pill-bar that floats directly under the AdminLayout sticky
 * header and lets the operator jump to any of 5 high-traffic sections
 * without scrolling a wall of cards.
 *
 *   [📈 Investor Pulse]  [📰 Newsletter Ops]  [🛡 Safety & Guardrails]
 *   [💼 Business at a Glance]  [📊 User Cohorts]
 *
 * Anchor contract — these `id`s MUST exist on the wrapper <div>s in
 * AdminHome.js (or the click is a no-op):
 *   #cc-investor-pulse, #cc-newsletter-ops, #cc-safety-guardrails,
 *   #cc-business-glance, #cc-user-cohorts
 *
 * Active-section highlighting uses IntersectionObserver so the
 * highlighted pill always tracks what's actually centered on screen
 * — no scroll-event polling, no jank.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  TrendingUp, Newspaper, ShieldAlert, Briefcase, BarChart3, Brain, Lightbulb, Atom, X, Keyboard, MapPin,
  HeartPulse, ClipboardCheck, MessageCircle, Sparkles, Crown, Lock, Target, DollarSign,
} from 'lucide-react';
import CommandCentreGuidedTour from './CommandCentreGuidedTour';

// ── Design vocabulary ────────────────────────────────────────────────
// Feb 2026 — Robert flagged the two chip rows as "different sizes shapes
// and colours … a bit disorganised". Every chip on both rows now shares:
//
//   • identical pill shape (`h-8 px-3.5 py-1.5 rounded-full`)
//   • identical `text-xs font-semibold` type
//   • an icon on the left, same 14px lucide glyph
//   • a colour-tinted rest state:
//       background = accent @ ~10% opacity
//       text       = accent @ 100%
//       border     = accent @ ~30% opacity
//   • solid accent + white text on hover / active
//
// One vocabulary → the CC skims like a single cohesive nav bar, and
// section colours propagate consistently from chip → section frame.

const CHIP_BASE_CLASSES =
  'inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-xs font-semibold ' +
  'whitespace-nowrap transition-all duration-150 select-none';

// Convert `#RRGGBB` → `rgba(r,g,b,alpha)` so we can build tint / border
// variants from a single hex accent without extra dependencies.
const hexToRgba = (hex, alpha) => {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return `rgba(107,114,128,${alpha})`; // safe grey fallback
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const chipRestStyle = (accent) => ({
  backgroundColor: hexToRgba(accent, 0.10),
  color: accent,
  border: `1px solid ${hexToRgba(accent, 0.30)}`,
});
const chipActiveStyle = (accent) => ({
  backgroundColor: accent,
  color: '#FFFFFF',
  border: `1px solid ${accent}`,
  boxShadow: `0 1px 3px ${hexToRgba(accent, 0.35)}`,
});

// Section registry — single source of truth for label, icon, anchor id,
// and brand accent color. Adding a new jump-link is a 1-line change.
const SECTIONS = [
  { id: 'cc-investor-pulse',    label: 'Investor Pulse',       icon: TrendingUp,   accent: '#7B4FD8', hotkey: 'i' },
  { id: 'cc-newsletter-ops',    label: 'Newsletter Ops',       icon: Newspaper,    accent: '#D97706', hotkey: 'n' },
  { id: 'cc-safety-guardrails', label: 'Safety & Guardrails',  icon: ShieldAlert,  accent: '#DC2626', hotkey: 's' },
  { id: 'cc-ava-behavior',      label: 'AvA Behavior',         icon: Brain,        accent: '#0891B2', hotkey: 'a' },
  { id: 'cc-rebecca-manager',   label: 'Rebecca Tutor',        icon: Atom,         accent: '#2563EB', hotkey: 'r' },
  { id: 'cc-voice-of-user',     label: 'Voice of User',        icon: Lightbulb,    accent: '#F5831F', hotkey: 'v' },
  { id: 'cc-business-glance',   label: 'Business at a Glance', icon: Briefcase,    accent: '#4338CA', hotkey: 'b' },
  { id: 'cc-user-cohorts',      label: 'User Cohorts',         icon: BarChart3,    accent: '#059669', hotkey: 'u' },
];

// Section-level registry — the second Jump-to row. Now also carries an
// icon (Feb 2026 redesign) so both rows are visually indistinguishable
// except for row label. Accent colours match the section-frame stripes
// so pill ↔ section reads as one continuous vocabulary.
const SECTION_JUMPS = [
  { id: 'cc-section-business-intelligence',   label: 'Business Intelligence',     icon: BarChart3,         accent: '#4338CA' },
  { id: 'cc-section-revenue-social-product',  label: 'Revenue · Social · Product',icon: DollarSign,        accent: '#F5831F' },
  { id: 'cc-section-growth-leads',            label: 'Growth & Leads',            icon: Target,            accent: '#10B981' },
  { id: 'cc-section-stability-health',        label: 'Stability & Health',        icon: HeartPulse,        accent: '#DC2626' },
  { id: 'cc-section-stabilisation',           label: 'Stabilisation Register',    icon: ClipboardCheck,    accent: '#4F46E5' },
  { id: 'cc-section-growth-signal',           label: 'Growth Signal',             icon: TrendingUp,        accent: '#059669' },
  { id: 'cc-section-product-ava',             label: 'Product & AvA',             icon: Sparkles,          accent: '#7C3AED' },
  { id: 'cc-section-community-voice',         label: 'Community & Voice',         icon: MessageCircle,     accent: '#0891B2' },
  { id: 'cc-section-newsletter-comms',        label: 'Newsletter & Comms',        icon: Newspaper,         accent: '#D97706' },
  { id: 'cc-section-founder-board',           label: 'Founder & Board',           icon: Crown,             accent: '#A16207' },
  { id: 'cc-section-security-admin',          label: 'Security & Admin',          icon: Lock,              accent: '#475569' },
];

// AdminLayout's <header> sits at sticky top:0 with py-3 padding and
// ~30-40px content. Empirically the header is ~57px tall. Subtract a
// little so the scroll target lands a hair below the sub-nav's bottom
// edge instead of being clipped under it.
const HEADER_OFFSET_PX = 57;
const SUBNAV_HEIGHT_PX = 52;
const SCROLL_GUTTER_PX = HEADER_OFFSET_PX + SUBNAV_HEIGHT_PX + 12;

const CommandCentreSubNav = () => {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const [tourOpen, setTourOpen] = useState(false);
  const scrollerRef = useRef(null);

  // IntersectionObserver wires the highlight pill to whichever section
  // currently dominates the viewport. We track the entry whose top
  // edge sits closest to the sub-nav's bottom edge (positive = below,
  // negative = scrolled past).
  useEffect(() => {
    const targets = SECTIONS
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);
    if (!targets.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry closest to the top of the viewport that is
        // still intersecting — gives a stable highlight as the user
        // scrolls slowly through the dashboard.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        // Treat the area between the sub-nav and 40% down the viewport
        // as the "active zone". rootMargin: top, right, bottom, left.
        rootMargin: `-${SCROLL_GUTTER_PX}px 0px -55% 0px`,
        threshold: [0, 0.1, 0.25],
      }
    );
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const jumpTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    // Feb 2026 — measure the sticky header + subnav LIVE at click time
    // instead of relying on hardcoded constants. Fixes an issue where
    // the target landed under the sticky sub-nav on production because
    // the real sub-nav rendered taller than the 52px estimate. Add a
    // 12px breathing gutter so the section title isn't clipped by the
    // bottom border of the sub-nav.
    let stickyBottom = HEADER_OFFSET_PX + SUBNAV_HEIGHT_PX;
    try {
      const subnav = document.querySelector('[data-testid="cc-subnav"]');
      if (subnav) {
        const r = subnav.getBoundingClientRect();
        // r.bottom is the pixel row where the sticky element ends
        // relative to the viewport top — exactly what we need.
        stickyBottom = Math.max(0, r.bottom);
      }
    } catch (e) { /* fall back to constants */ }
    const gutter = stickyBottom + 12;
    const top = el.getBoundingClientRect().top + window.scrollY - gutter;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    setActiveId(id);
  }, []);

  // Feb 2026 — Keyboard shortcut: `g` then a letter (GitHub / Linear
  // style) jumps to a section. Idle > 1200ms resets the leader. Never
  // fires when the user is typing in an <input>, <textarea>, contentEditable
  // element, or when a modifier (Cmd/Ctrl/Alt/Meta) is held so it can
  // never collide with browser shortcuts. See `SECTIONS[i].hotkey`.
  useEffect(() => {
    let leader = false;
    let leaderTimer = null;
    const clearLeader = () => {
      leader = false;
      if (leaderTimer) { clearTimeout(leaderTimer); leaderTimer = null; }
    };
    const isTyping = (ev) => {
      const t = ev.target;
      if (!t) return false;
      const tag = (t.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || t.isContentEditable;
    };
    const onKey = (ev) => {
      if (ev.ctrlKey || ev.metaKey || ev.altKey) return;
      if (isTyping(ev)) return;
      const k = (ev.key || '').toLowerCase();
      if (!leader) {
        if (k === 'g') {
          leader = true;
          leaderTimer = setTimeout(clearLeader, 1200);
        }
        return;
      }
      // Leader active — try to resolve to a section.
      const match = SECTIONS.find((s) => s.hotkey === k);
      clearLeader();
      if (match) {
        ev.preventDefault();
        jumpTo(match.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (leaderTimer) clearTimeout(leaderTimer);
    };
  }, [jumpTo]);

  return (
    <div
      className="sticky bg-white -mx-3 sm:-mx-6 px-3 sm:px-6 mb-6"
      style={{
        top: `${HEADER_OFFSET_PX}px`,
        zIndex: 25,
        borderBottom: '1px solid #E0E0E0',
        boxShadow: '0 1px 0 rgba(0,0,0,0.02)',
      }}
      data-testid="cc-subnav"
    >
      {/* Row 1 — JUMP TO
          Feb 2026 restructure: heading sits ABOVE the chips (not inline
          before them). Reasons:
          • chips wrap flush to the left edge, so Business at a Glance
            now aligns exactly under Investor Pulse.
          • the heading can be larger + bold without eating chip width.
          • Take Tour gets its own home in the top-right of the heading
            row, which no longer looks "lost" bobbing at the end of the
            wrapped chip row.
      */}
      <div className="pt-3 pb-2">
        <div className="flex items-center justify-between mb-2">
          <span
            className="text-sm font-bold uppercase tracking-wide"
            style={{ color: '#374151' }}
            data-testid="cc-subnav-heading-jump"
          >
            Jump to
          </span>
          <button
            type="button"
            onClick={() => setTourOpen(true)}
            className={CHIP_BASE_CLASSES}
            style={chipActiveStyle('#7B4FD8')}
            title="Take a 60-second guided walk-through of every Command Centre product"
            data-testid="cc-subnav-guided-tour"
          >
            <MapPin size={14} />
            Take Tour
          </button>
        </div>
        <nav
          ref={scrollerRef}
          className="flex items-center flex-wrap gap-1.5 gap-y-2"
          aria-label="Command Centre quick navigation"
        >
          {SECTIONS.map(({ id, label, icon: Icon, accent, hotkey }) => {
            const isActive = activeId === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => jumpTo(id)}
                title={hotkey ? `${label} · shortcut: g then ${hotkey.toUpperCase()}` : label}
                className={CHIP_BASE_CLASSES}
                style={isActive ? chipActiveStyle(accent) : chipRestStyle(accent)}
                onMouseEnter={(e) => {
                  if (!isActive) Object.assign(e.currentTarget.style, chipActiveStyle(accent));
                }}
                onMouseLeave={(e) => {
                  if (!isActive) Object.assign(e.currentTarget.style, chipRestStyle(accent));
                }}
                data-testid={`cc-subnav-link-${id}`}
                aria-current={isActive ? 'true' : undefined}
              >
                <Icon size={14} />
                {label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Row 2 — SECTIONS
          Same structure: bold heading above, chips wrap flush left so
          Founder & Board lines up directly under Stability & Health. */}
      <div className="pb-3">
        <div className="mb-2">
          <span
            className="text-sm font-bold uppercase tracking-wide"
            style={{ color: '#374151' }}
            data-testid="cc-subnav-heading-sections"
          >
            Sections
          </span>
        </div>
        <nav
          className="flex items-center flex-wrap gap-1.5 gap-y-2"
          aria-label="Command Centre section jump"
          data-testid="cc-subnav-sections"
        >
          {SECTION_JUMPS.map(({ id, label, icon: Icon, accent }) => (
            <button
              key={id}
              type="button"
              onClick={() => jumpTo(id)}
              title={`Jump to ${label}`}
              className={CHIP_BASE_CLASSES}
              style={chipRestStyle(accent)}
              onMouseEnter={(e) => Object.assign(e.currentTarget.style, chipActiveStyle(accent))}
              onMouseLeave={(e) => Object.assign(e.currentTarget.style, chipRestStyle(accent))}
              data-testid={`cc-subnav-section-${id}`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </nav>
      </div>
      <CommandCentreGuidedTour open={tourOpen} onClose={() => setTourOpen(false)} />
    </div>
  );
};

export default CommandCentreSubNav;
